/**
 * Edits the wall texture editor makes to a region in its EDITOR shape
 * (`wallRegionFormat.savedRegionToEditor`). Pure.
 */
import { isGeneratedKitchenBacksplash } from '@shared-lib/kitchen-assembly/styling/kitchenBacksplash';

/** Is this region wearing no finish yet (a draft nobody tiled)? */
const hasNoFinish = (r) => !r?.textureId && !r?.paintData;

/** Does `next` put a different finish on the region than it wears? */
const changesFinish = (existing, { textureId, paintData }) =>
  (paintData?.id ?? null) !== (existing?.paintData?.id ?? null) ||
  (paintData ? false : textureId !== existing?.textureId);

/**
 * The region with a finish (and bounds) applied, every other marker kept.
 *
 * This used to rebuild the region from a fixed list of fields, so a pick
 * dropped whatever the list did not name — a backsplash band's `kind` and
 * provenance, a shower tile's `tileHeight` — and the save wrote the region
 * without them.
 *
 * A pick that CHANGES the finish makes a band drawn in the default tile a
 * chosen one for good (`unchosenTextureId`, see `wallRegionFormat`): going
 * back to the first tile later in the session is still a choice, and the
 * other walls the first pick reached are already chosen. A drag — same
 * finish, new bounds — keeps it as it was.
 */
export const applyFinishToEditorRegion = (existingRegion, finish) => {
  const { region, textureId, paintData } = finish;
  const next = { ...existingRegion, region, textureId, paintData };
  if (next.unchosenTextureId && changesFinish(existingRegion, finish)) {
    next.unchosenTextureId = null;
  }
  return next;
};

/**
 * Does a finish picked on the backsplash piece `picked` also go on the piece
 * `other` on the same wall?
 *
 * A generated kitchen backsplash is two bands — over the counter and behind
 * the range — tiled separately (Fixtures & Finishes has a row for each), yet
 * every piece of both shares one `groupId`. So two pieces are the same band
 * only when they are the same KIND; a piece without one (a hand-drawn
 * backsplash) follows the rest as it always has.
 */
export const isSameBacksplashBand = (picked, other) =>
  picked?.surfaceType === 'backsplash' &&
  other?.surfaceType === 'backsplash' &&
  (picked.kind ?? null) === (other.kind ?? null);

/**
 * Does a finish picked on the backsplash piece `picked` also go on the piece
 * `sibling` on the SAME wall? The same band does; so does a backsplash draft
 * with no finish yet, of either kind — Add Backsplash opens the editor on
 * drafts of both kinds, and a draft left untiled is never saved, so the wall
 * would lose that band. A band that already wears a tile never crosses kinds.
 */
export const takesBacksplashPick = (picked, sibling) =>
  isSameBacksplashBand(picked, sibling) ||
  (picked?.surfaceType === 'backsplash' &&
    sibling?.surfaceType === 'backsplash' &&
    hasNoFinish(sibling));

const SPECIALTY_SURFACE_TYPES = ['backsplash', 'showerWall', 'tubBacksplash'];
const isShowerSection = (r) =>
  r.surfaceType === 'showerWall' || r.source === 'shower';

/**
 * Edit wall's section groups, and the bands it does NOT list: a generated
 * kitchen backsplash is edited on the wall panel's Backsplash card
 * (contractor call #9), so its pieces appear in no group. They stay in the
 * editor's regions — `compose` puts them back into whatever a reorder saves,
 * or the save would drop them and the bands would be deleted.
 *
 * @returns {{wall, shower, backsplash, tub, hidden, compose}}
 *   `compose({ wall?, shower?, backsplash?, tub? })` — the full region list
 *   after one group was reordered, hidden bands included
 */
export function editorSections(regions) {
  const hidden = [];
  const groups = { wall: [], shower: [], backsplash: [], tub: [] };
  for (const r of regions || []) {
    if (isGeneratedKitchenBacksplash(r)) hidden.push(r);
    else if (isShowerSection(r)) groups.shower.push(r);
    else if (r.surfaceType === 'backsplash') groups.backsplash.push(r);
    else if (r.surfaceType === 'tubBacksplash') groups.tub.push(r);
    else if (!SPECIALTY_SURFACE_TYPES.includes(r.surfaceType))
      groups.wall.push(r);
  }
  const compose = (next) => [
    ...(next.wall ?? groups.wall),
    ...(next.shower ?? groups.shower),
    ...(next.backsplash ?? groups.backsplash),
    ...(next.tub ?? groups.tub),
    ...hidden,
  ];
  return { ...groups, hidden, compose };
}
